import * as i0 from '@angular/core';
import { Injectable, NgZone, NgModule } from '@angular/core';
import { WellKnownMimeTypes, SpringRSocketMessagingBuilder } from 'rsocket-rxjs';
import { RSocketState } from 'rsocket-rxjs/dist/lib/api/rsocket.api';
import { Observable, Subject, ReplaySubject } from 'rxjs';
import { concatMap, map, switchMap, startWith, filter, take } from 'rxjs/operators';

function zoneFix(zone) {
    return (source) => {
        return new Observable(observer => {
            const onNext = (value) => zone.run(() => observer.next(value));
            const onError = (e) => zone.run(() => observer.error(e));
            const onComplete = () => zone.run(() => observer.complete());
            return source.subscribe(onNext, onError, onComplete);
        });
    };
}
class FluentRequest {
    constructor(_zone, socketHolder, _route, _data, _outgoingMimeType, _incomingMimeType, _authentication, _requester) {
        this._zone = _zone;
        this.socketHolder = socketHolder;
        this._route = _route;
        this._data = _data;
        this._outgoingMimeType = _outgoingMimeType;
        this._incomingMimeType = _incomingMimeType;
        this._authentication = _authentication;
        this._requester = _requester;
    }
    data(data) {
        this._data = data;
        return this.copy();
    }
    copy() {
        return new FluentRequest(this._zone, this.socketHolder, this._route, this._data, this._outgoingMimeType, this._incomingMimeType, this._authentication, this._requester);
    }
    requestMimetype(type) {
        return new FluentRequest(this._zone, this.socketHolder, this._route, this._data, type, this._incomingMimeType, this._authentication, this._requester);
    }
    answerMimetype(type) {
        return new FluentRequest(this._zone, this.socketHolder, this._route, this._data, this._outgoingMimeType, type, this._authentication, this._requester);
    }
    authentication(auth) {
        return new FluentRequest(this._zone, this.socketHolder, this._route, this._data, this._outgoingMimeType, this._incomingMimeType, auth, this._requester);
    }
    requester(requester) {
        this._requester = requester;
        return this.copy();
    }
    requestResponse() {
        return this.socketHolder.pipe(concatMap(socket => socket.requestResponse({
            route: this._route,
            authentication: this._authentication,
            data: this._data,
            dataMimeType: this._outgoingMimeType,
        }, {
            responseDataMimeType: this._incomingMimeType
        })), map(payload => payload.data));
    }
    requestStream() {
        return this.socketHolder.pipe(concatMap(socket => socket.requestStream({
            route: this._route,
            data: this._data,
            dataMimeType: this._outgoingMimeType,
            authentication: this._authentication,
        }, undefined, {
            responseDataMimeType: this._incomingMimeType
        })), map(payload => payload.data));
    }
    fireAndForget() {
        this.socketHolder.subscribe({
            next: socket => {
                socket.requestFNF({
                    route: this._route,
                    data: this._data,
                    dataMimeType: this._outgoingMimeType
                });
            }
        });
    }
}

class RSocketServiceOptions {
    constructor(config, url, reconnectTimeout) {
        this.config = config;
        this.url = url;
        this.reconnectTimeout = reconnectTimeout;
    }
}
class RSocketService {
    constructor(ngZone) {
        this.ngZone = ngZone;
        this.$destroy = new Subject();
        this._socket = new ReplaySubject(1);
        this._defaultIncomingMimeType = WellKnownMimeTypes.APPLICATION_JSON.name;
        this._defaultOutgoingMimeType = WellKnownMimeTypes.APPLICATION_JSON.name;
    }
    connect(config) {
        let builder = new SpringRSocketMessagingBuilder();
        if (config.dataMimeType != undefined) {
            builder = builder.dataMimeType(config.dataMimeType);
            this._defaultIncomingMimeType = config.dataMimeType;
            this._defaultOutgoingMimeType = config.dataMimeType;
        }
        if (config.maxLifetime != undefined) {
            builder = builder.maxLifetime(config.maxLifetime);
        }
        if (config.keepaliveTime != undefined) {
            builder = builder.keepaliveTime(config.keepaliveTime);
        }
        if (config.connectMappingData != undefined) {
            builder = builder.connectMappingData(config.connectMappingData);
        }
        if (config.connectMappingRoute != undefined) {
            builder = builder.connectMappingRoute(config.connectMappingRoute);
        }
        if (config.reconnectTimeout !== undefined) {
            builder = builder.automaticReconnect(config.reconnectTimeout);
        }
        else {
            builder = builder.automaticReconnect();
        }
        builder = builder.connectionString(config.url);
        if (config.builderCustomizer) {
            config.builderCustomizer(builder);
        }
        this._subscription = builder.build().subscribe((socket) => {
            this._socket.next(socket);
        });
    }
    ngOnDestroy() {
        this._subscription.unsubscribe();
    }
    // to enable this code we need to public RSocketState in rsocket-rxjs library
    state() {
        return this._socket.pipe(switchMap(v => {
            return v.state();
        }), startWith(RSocketState.Disconnected));
    }
    route(route) {
        return new FluentRequest(this.ngZone, this.getNextConnectedSocket(), route, null, this._defaultOutgoingMimeType, this._defaultIncomingMimeType);
    }
    getNextConnectedSocket() {
        return this._socket.pipe(switchMap(socket => socket.state().pipe(
        //@ts-ignore
        filter(state => state == RSocketState.Connected), map(state => socket))), take(1));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: RSocketService, deps: [{ token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: RSocketService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: RSocketService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i0.NgZone }]; } });

class RSocketRxjsModule {
    static forRoot(config) {
        const serviceFactory = (zone) => {
            const s = new RSocketService(zone);
            s.connect(config);
            return s;
        };
        return {
            ngModule: RSocketRxjsModule,
            providers: [
                {
                    provide: RSocketService,
                    useFactory: serviceFactory,
                    deps: [NgZone],
                }
            ]
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: RSocketRxjsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.10", ngImport: i0, type: RSocketRxjsModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: RSocketRxjsModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: RSocketRxjsModule, decorators: [{
            type: NgModule,
            args: [{}]
        }] });

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/**
 * Generated bundle index. Do not edit.
 */

export { RSocketRxjsModule, RSocketService, RSocketServiceOptions };
